import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton, Paper, Stack, Typography } from '@mui/material';

import { isRowApproved, isRowManuallyApproved } from '../../manualOverridePersistence';
import ApprovalStatusChip from '../../reportCards/ApprovalStatusChip';
import { formatPrice } from '../../shared/checklistUtils';
import type { ManualOverrideState } from '../../types';

import InclusionRow, { INCLUSION_ROW_GRID_TEMPLATE_COLUMNS } from './InclusionRow';
import InclusionRowHeader from './InclusionRowHeader';
import type { DealOptionsInclusionsDealRow } from './types';

interface Props {
  deal: DealOptionsInclusionsDealRow;
  expandedRows: Record<string, boolean>;
  manualOverrides: ManualOverrideState;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (rowId: string, defaultOpen?: boolean) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function DealCard({
  deal,
  expandedRows,
  manualOverrides,
  showAutoApproved,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  const pendingCount = deal.inclusions.filter((inc) => !isRowApproved(inc, manualOverrides)).length;
  const isOpen = expandedRows[deal.id] ?? true;

  const visibleInclusions = deal.inclusions.filter((inc) => {
    if (showAutoApproved) {
      return true;
    }
    return !isRowApproved(inc, manualOverrides);
  });

  const metrics = [
    { label: 'Sell price', value: formatPrice(deal.sellPrice, deal.currency) },
    { label: 'Cost price', value: formatPrice(deal.costPrice, deal.currency) },
    { label: '% margin', value: deal.marginPercent != null ? `${deal.marginPercent}%` : '—' },
    { label: 'Rack rate', value: formatPrice(deal.rackRate, deal.currency) },
    { label: 'Room', value: deal.roomName || '—' },
  ];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1.25, overflow: 'hidden' }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => onToggleDetails(deal.id, true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggleDetails(deal.id, true);
          }
        }}
        sx={{
          alignItems: 'center',
          columnGap: 2,
          cursor: 'pointer',
          display: 'grid',
          gridTemplateColumns: INCLUSION_ROW_GRID_TEMPLATE_COLUMNS,
          p: 2,
          '&:hover': { bgcolor: 'grey.50' },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {deal.dealOptionName || '—'}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            <ApprovalStatusChip
              approved={pendingCount === 0}
              count={pendingCount > 0 ? pendingCount : deal.inclusions.length}
            />
            <Typography variant="body2" color="text.secondary">
              {deal.inclusions.length} inclusion{deal.inclusions.length === 1 ? '' : 's'}
            </Typography>
          </Stack>
        </Box>

        {metrics.map((metric) => (
          <Box key={metric.label} onClick={(event) => event.stopPropagation()}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{ display: 'block', letterSpacing: 0.6 }}
            >
              {metric.label.toUpperCase()}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {metric.value}
            </Typography>
          </Box>
        ))}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton
            size="small"
            sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={isOpen}>
        {visibleInclusions.length === 0 ? (
          <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {deal.inclusions.length === 0 ? 'No inclusions configured.' : 'All inclusions approved.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
            <InclusionRowHeader />
            {visibleInclusions.map((inc) => (
              <InclusionRow
                key={inc.id}
                currency={deal.currency}
                inc={inc}
                approved={isRowApproved(inc, manualOverrides)}
                manuallyApproved={isRowManuallyApproved(inc, manualOverrides)}
                onApprovalChange={onApprovalChange}
                onUnapprovalChange={onUnapprovalChange}
              />
            ))}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}
